import FormatService from "services/formatService";
import { LOW_MARGIN_THRESHOLD } from "./PricingByClientConst";
import { ActivityItemPricingType } from "../PricingManagementConsts";

/**
 * Builds the pricing matrix from API data, filtered client-side by selectedLotIds.
 * Returns { rows, lotIdsFromData } where lotIdsFromData is the Set of lot IDs
 * that actually appear in the filtered results.
 */
export const buildPricingMatrixData = ({
    activityPricing,
    selectedLotIds,
    activityItemLookup,
    vendorLookup,
    bundlesLookup,
}) => {
    if (!activityPricing) {
        return { rows: [], lotIdsFromData: new Set() };
    }

    const hasLotFilter = selectedLotIds?.length > 0;

    const pricingMap = {};
    const activityItemsSet = new Set();
    const bundlesSet = new Set();
    const lotIdsFromData = new Set();

    activityPricing.forEach((pricing) => {
        if (hasLotFilter && !selectedLotIds.includes(pricing.lotId)) {
            return;
        }

        if (pricing.activityItemId) {
            activityItemsSet.add(pricing.activityItemId);
        }
        if (pricing.bundleId) {
            bundlesSet.add(pricing.bundleId);
        }
        lotIdsFromData.add(pricing.lotId);

        const key = `${pricing.activityItemId ? `activity_${pricing.activityItemId}` : `bundle_${pricing.bundleId}`}_${pricing.lotId}`;
        if (!pricingMap[key]) {
            pricingMap[key] = {
                ...pricing,
                activityName: pricing.activityItemId ? activityItemLookup?.[pricing.activityItemId]?.name || "Unknown Activity" : bundlesLookup?.[pricing.bundleId]?.name || "Unknown Bundle",
                formattedCharge: FormatService.formatCurrency(pricing.charge),
                isPMFVendor: vendorLookup?.[pricing.vendorId]?.name === "PMF",
                priceVariance: pricing.priceVarianceData?.sameClientOtherLots?.length > 0 || pricing.priceVarianceData?.otherClientsSameLot?.length > 0,
                pricingType: pricing.activityItemId ? activityItemLookup?.[pricing.activityItemId]?.pricingType : ActivityItemPricingType.SIMPLE
            };
        }
    });

    let activityRows = Object.keys(activityItemLookup || {}).map((activityItemId) => {
        const row = {
            id: activityItemId,
            activityItemId,
            activityName: activityItemLookup?.[activityItemId]?.name || "Unknown Activity",
        };

        lotIdsFromData.forEach((lotId) => {
            const key = `activity_${activityItemId}_${lotId}`;
            row[`lot_${lotId}`] = pricingMap[key] || null;
        });

        return row;
    });

    let bundleRows = Object.keys(bundlesLookup || {}).map((bundleId) => {
        const row = {
            id: bundleId,
            bundleId,
            activityName: bundlesLookup?.[bundleId]?.name || "Unknown Bundle",
        };
        lotIdsFromData.forEach((lotId) => {
            const key = `bundle_${bundleId}_${lotId}`;
            row[`lot_${lotId}`] = pricingMap[key] || null;
        });
        return row;
    });

    const rows = [...activityRows, ...bundleRows].sort((a, b) => a.activityName.localeCompare(b.activityName));

    return { rows, lotIdsFromData };
};

export const getPricingCellStyle = ({ row, lotId }) => {
    const pricingData = row[`lot_${lotId}`];

    if (!pricingData) {
        return {
            color: "#5b5b5b",
            background: "#f2f5f9",
        };
    }

    // Activities without a preferred vendor selected are left unstyled (the cell
    // shows a "Set vendor" call-to-action instead). Bundles have no vendor concept,
    // so this guard never applies to them — their margin/variance coloring stands.
    const isBundle = !!pricingData.bundleId;
    if (!isBundle && pricingData.hasPricingCombinations && !pricingData.vendorId && !pricingData.preferredVendorId) {
        return {
            color: "#5b5b5b",
            background: "#f2f5f9",
        };
    }

    if (pricingData.margin !== null && pricingData.margin < LOW_MARGIN_THRESHOLD) {
        return {
            background: "#ffebee",
        };
    }

    if (pricingData.priceVariance) {
        return {
            background: "#fff3cd",
        };
    }

    return {
        fontWeight: pricingData.isPMFVendor ? 500 : undefined,
    };
};